import { CheckoutProfile, CheckoutProfileFulfillment } from '@commerce-checkout-webcomponents/sdk';
import { checkoutShippingOptions } from '@commerce-checkout-webcomponents/ui';
import { FulfillmentApi, WarehouseCollection } from '@commerce-fulfillment-webcomponents/sdk';
import type { Session } from '@nextgen-web-framework/all';
import { StorageService, Logger } from '@nextgen-web-framework/core';
import { WebEvents, WebEventsEnum } from '@commerce-webcomponents/sdk';
import { LocalStorageKeys } from '../../ui/utils/common';

const logger = Logger.getInstance();

// Type guard to check if the object is a CheckoutProfile
function isCheckoutProfileResponse(checkoutProfile: CheckoutProfile): checkoutProfile is CheckoutProfile {
  return 'fulfillmentType' in checkoutProfile;
}

const storeCheckoutProfile = async (
  profileData: CheckoutProfile,
  updateSession: (session: Session) => void,
  updateAddress?: () => void,
) => {
  const config = ConfigService.getConfigs();
  const storeId = config.NEXT_PUBLIC_WAREHOUSE_ID as string;
  const isFulfillmentAPIDisable = config.NEXT_PUBLIC_FULFILLMENT_API_DISABLE as boolean;
  try {
    if (!isCheckoutProfileResponse(profileData)) {
      throw new Error('The provided data does not have a fulfillment type');
    }

    const { fulfillmentType } = profileData;
    const localStorage = StorageService.getLocalStorage();
    const warehouseId = await fetchWareHouseId(storeId, fulfillmentType, isFulfillmentAPIDisable);
    fulfillmentType.warehouse = warehouseId;
    localStorage.store(LocalStorageKeys.CHECKOUT_PROFILE, profileData);
    localStorage.store(LocalStorageKeys.WAREHOUSE_ID, warehouseId);

    WebEvents.publishEvent({
      event: WebEventsEnum.WarehouseIdUpdated,
      payload: {},
      sync: false,
    });

    if (updateAddress) {
      updateAddress();
    }
    updateSession({ warehouseId });
  } catch (error) {
    logger.error('profile: Failed to save checkout profile data', error);
  }
};

const fetchWareHouseId = async (
  storeId: string,
  fulfillmentType: CheckoutProfileFulfillment,
  isFulfillmentAPIDisable: boolean,
) => {
  const { id } = fulfillmentType;
  let warehouseId = storeId;
  const baseUrl = ApiConfigService.getBaseUrl();
  if (!isFulfillmentAPIDisable) {
    if (id === checkoutShippingOptions.BOPIS) {
      warehouseId = fulfillmentType.serviceLocation?.id ?? storeId;
    } else {
      const postalCode =
        id === checkoutShippingOptions.HOMEDELIVERY
          ? fulfillmentType.serviceLevel?.deliveryAddress?.daneCode ||
            fulfillmentType.serviceLevel?.deliveryAddress?.postalCode
          : fulfillmentType.serviceLocation?.address?.postCode;
      try {
        const response = (await FulfillmentApi.getWarehousesByPostCode({
          baseUrl,
          postCode: postalCode,
        })) as WarehouseCollection;
        warehouseId = response?.warehouses?.[0]?.id ?? storeId;
      } catch (error) {
        logger.error('profile: Warehouse API failed', error);
      }
    }
  }

  return warehouseId;
};

export default storeCheckoutProfile;
